/**
 * Light / dark / system theme. Light is the default whatever the OS says; dark is opt-in (the header
 * toggle), or follows the OS when the user picks "system". The inline script in index.html applies
 * the stored choice before the first paint with the same rules (theme.test.ts checks they agree);
 * this module keeps <html data-theme> and the browser chrome colour in step afterwards.
 */
import { readStorage, THEME_KEY, writeStorage } from '../lib/storage';

export type ThemePref = 'light' | 'dark' | 'system';
export type Scheme = 'light' | 'dark';

/** Toggle order: light → dark → system → light. */
export const THEME_CYCLE: readonly ThemePref[] = ['light', 'dark', 'system'];

/** Browser chrome colour (meta theme-color): the header surface, `--c-surface` in tokens.css. */
export const THEME_COLOR: Record<Scheme, string> = { light: '#fbf8f2', dark: '#24201d' };

const SYSTEM_DARK = '(prefers-color-scheme: dark)';

/** A stored value, validated: anything but 'dark' or 'system' (missing, stale, tampered) is light. */
export function parseThemePref(stored: string | null | undefined): ThemePref {
  return stored === 'dark' || stored === 'system' ? stored : 'light';
}

/** The scheme actually shown: the choice itself, or the OS preference for "system". */
export function resolveTheme(stored: string | null | undefined, systemDark: boolean): { pref: ThemePref; scheme: Scheme } {
  const pref = parseThemePref(stored);
  const scheme: Scheme = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
  return { pref, scheme };
}

export function nextThemePref(pref: ThemePref): ThemePref {
  return THEME_CYCLE[(THEME_CYCLE.indexOf(pref) + 1) % THEME_CYCLE.length];
}

export function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(SYSTEM_DARK).matches;
  } catch {
    return false;
  }
}

export function readThemePref(): ThemePref {
  return parseThemePref(readStorage(THEME_KEY));
}

export function writeThemePref(pref: ThemePref): void {
  writeStorage(THEME_KEY, pref);
}

/**
 * Sets <html data-theme> (tokens.css: 'light', 'dark', or 'system' = follow the media query) and the
 * browser chrome colours for the scheme shown.
 */
export function applyTheme(pref: ThemePref, scheme: Scheme, doc: Document = document): void {
  doc.documentElement.setAttribute('data-theme', pref);
  doc.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[scheme]);
  doc.querySelector('meta[name="color-scheme"]')?.setAttribute('content', pref === 'system' ? 'light dark' : pref);
}

/** Calls `onChange(dark)` when the OS switches between light and dark; returns the unsubscribe function. */
export function watchSystemScheme(onChange: (dark: boolean) => void): () => void {
  try {
    const query = window.matchMedia(SYSTEM_DARK);
    const listener = (e: MediaQueryListEvent) => onChange(e.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  } catch {
    return () => undefined;
  }
}
